import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { 
  Heart, 
  Eye, 
  MessageSquare,
  Check,
  Move,
  FileText,
  Target,
  Music,
  Zap,
  Trash2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Users
} from 'lucide-react';
import { MoveDialog } from './MoveDialog';
import { knowledgeApi, KnowledgeItem } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

interface ReelComment {
  text: string;
  ownerUsername: string;
  timestamp: string;
  repliesCount: number;
  replies: ReelComment[];
  likesCount: number;
}

interface Reel {
  id?: string;
  inputUrl?: string;
  caption: string;
  transcription: string;
  videoDescription?: string;
  keyPoint?: string;
  keyMessage?: string;
  hook?: string;
  improvedContent?: string;
  aidaAnalysis?: {
    attention?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
    interest?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
    desire?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
    action?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
    Attention?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
    Interest?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
    Desire?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
    Action?: { score?: number; analysis?: string; Score?: number; Analysis?: string };
  };
  hashtags: string[];
  mentions: string[];
  url: string;
  commentsCount: number;
  latestComments?: ReelComment[];
  comments?: ReelComment[];
  videoUrl: string;
  likesCount: number;
  videoPlayCount: number;
  timestamp: string;
  ownerFullName: string;
  ownerUsername: string;
  ownerId: string;
  followersCount?: number;
  productType: string;
  videoDuration: number;
  isSponsored: boolean;
  musicInfo: {
    artist_name?: string;
    song_name?: string;
    artistName?: string;
    songName?: string;
    usesOriginalAudio?: boolean;
    shouldMuteAudio?: boolean;
    shouldMuteAudioReason?: string;
    audioId?: string;
  };
}

interface ReelsListProps {
  reels: Reel[];
  sourceId?: string | null;
  onReelsDeleted?: () => void;
  items?: KnowledgeItem[];
  onPreventLoadItems?: (prevent: boolean) => void;
  onUpdateContent?: () => Promise<void>;
}

interface DeleteDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
}

interface ReelAnalysisDetailsProps {
  reel: Reel;
}

interface ReelTranscriptionDetailsProps {
  reel: Reel;
}

// Компонент для отображения анализа AIDA под спойлером
function ReelAnalysisDetails({ reel }: ReelAnalysisDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!reel.aidaAnalysis) {
    return null;
  }

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center w-full text-left p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <BarChart3 className="w-3 h-3 mr-1 text-orange-500 dark:text-orange-400" />
        <span className="text-xs font-medium text-orange-500 dark:text-orange-400 flex-1">Анализ</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pl-4 space-y-3">
              {reel.aidaAnalysis && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">AIDA Анализ:</div>
                  
                  {(reel.aidaAnalysis.Attention || reel.aidaAnalysis.attention) && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">Внимание (A)</span>
                        <span className="text-xs font-bold text-red-600 dark:text-red-400">
                          {(reel.aidaAnalysis.Attention?.Score || reel.aidaAnalysis.attention?.score)}/10
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300" style={{ fontSize: '0.8125rem' }}>
                        {reel.aidaAnalysis.Attention?.Analysis || reel.aidaAnalysis.attention?.analysis}
                      </div>
                    </div>
                  )}
                  
                  {(reel.aidaAnalysis.Interest || reel.aidaAnalysis.interest) && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Интерес (I)</span>
                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                          {(reel.aidaAnalysis.Interest?.Score || reel.aidaAnalysis.interest?.score)}/10
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300" style={{ fontSize: '0.8125rem' }}>
                        {reel.aidaAnalysis.Interest?.Analysis || reel.aidaAnalysis.interest?.analysis}
                      </div>
                    </div>
                  )}
                  
                  {(reel.aidaAnalysis.Desire || reel.aidaAnalysis.desire) && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">Желание (D)</span>
                        <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400">
                          {(reel.aidaAnalysis.Desire?.Score || reel.aidaAnalysis.desire?.score)}/10
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300" style={{ fontSize: '0.8125rem' }}>
                        {reel.aidaAnalysis.Desire?.Analysis || reel.aidaAnalysis.desire?.analysis}
                      </div>
                    </div>
                  )}
                  
                  {(reel.aidaAnalysis.Action || reel.aidaAnalysis.action) && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">Действие (A)</span>
                        <span className="text-xs font-bold text-green-600 dark:text-green-400">
                          {(reel.aidaAnalysis.Action?.Score || reel.aidaAnalysis.action?.score)}/10
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300" style={{ fontSize: '0.8125rem' }}>
                        {reel.aidaAnalysis.Action?.Analysis || reel.aidaAnalysis.action?.analysis}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Компонент для отображения улучшенного контента под спойлером
function ReelImprovedContent({ reel }: ReelAnalysisDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!reel.improvedContent) {
    return null;
  }

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center w-full text-left p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <Zap className="w-3 h-3 mr-1 text-purple-500 dark:text-purple-400" />
        <span className="text-xs font-medium text-purple-500 dark:text-purple-400 flex-1">Советы по улучшению</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pl-3">
              <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {reel.improvedContent}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Функция для парсинга транскрипции со спикерами
function parseTranscriptionWithSpeakers(transcription: string) {
  // Заменяем \n на реальные переносы строк
  const textWithNewlines = transcription.replace(/\\n/g, '\n');
  
  // Паттерн для поиска спикеров: ищем \nИмя: или начало строки Имя:
  // где Имя - короткое слово (до 30 символов) без двоеточий
  // Это гарантирует, что мы находим только имена спикеров после переноса строки,
  // а не двоеточия в середине текста
  const speakerPattern = /(\n|^)([^\n:]{1,30}?):\s*/g;
  
  type ParsedPart = { type: 'speaker' | 'text' | 'newline'; content: string };
  const result: ParsedPart[] = [];
  const matches: Array<{ index: number; name: string; fullMatch: string; hasNewline: boolean }> = [];
  let match;
  
  // Находим все совпадения
  while ((match = speakerPattern.exec(textWithNewlines)) !== null) {
    matches.push({
      index: match.index,
      name: match[2].trim(),
      fullMatch: match[0],
      hasNewline: match[1] === '\n'
    });
  }
  
  // Если не найдено ни одного спикера, возвращаем весь текст как обычный текст
  if (matches.length === 0) {
    const lines = textWithNewlines.split('\n');
    lines.forEach((line, lineIndex) => {
      if (line.trim()) {
        result.push({
          type: 'text',
          content: line
        });
      }
      if (lineIndex < lines.length - 1) {
        result.push({
          type: 'newline',
          content: '\n'
        });
      }
    });
    return result;
  }
  
  // Обрабатываем текст до первого спикера
  const firstMatch = matches[0];
  if (firstMatch.index > 0) {
    const textBefore = textWithNewlines.substring(0, firstMatch.index);
    if (textBefore.trim()) {
      const linesBefore = textBefore.split('\n');
      linesBefore.forEach((line, lineIndex) => {
        if (line.trim()) {
          result.push({
            type: 'text',
            content: line
          });
        }
        if (lineIndex < linesBefore.length - 1) {
          result.push({
            type: 'newline',
            content: '\n'
          });
        }
      });
    }
  }
  
  // Обрабатываем все найденные спикеры
  for (let i = 0; i < matches.length; i++) {
    const currentMatch = matches[i];
    const nextMatch = matches[i + 1];
    
    // Добавляем перенос строки перед именем спикера (если он был в паттерне)
    if (currentMatch.hasNewline) {
      result.push({
        type: 'newline',
        content: '\n'
      });
    }
    
    // Добавляем имя спикера
    result.push({
      type: 'speaker',
      content: currentMatch.name
    });
    
    // Определяем текст спикера (от конца совпадения до следующего спикера или конца текста)
    const textStart = currentMatch.index + currentMatch.fullMatch.length;
    const textEnd = nextMatch ? nextMatch.index : textWithNewlines.length;
    const speakerText = textWithNewlines.substring(textStart, textEnd);
    
    if (speakerText.trim()) {
      result.push({
        type: 'text',
        content: speakerText
      });
    }
  }
  
  return result;
}

// Компонент для отображения транскрипции под спойлером
function ReelTranscriptionDetails({ reel }: ReelTranscriptionDetailsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!reel.transcription) {
    return null;
  }

  const parsedTranscription = parseTranscriptionWithSpeakers(reel.transcription);

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center w-full text-left p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <FileText className="w-3 h-3 mr-1 text-purple-500 dark:text-purple-400" />
        <span className="text-xs font-medium text-purple-500 dark:text-purple-400 flex-1">Транскрипция</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pl-4">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {parsedTranscription.map((part, index) => {
                  if (part.type === 'speaker') {
                    return (
                      <span key={index} className="text-purple-500 dark:text-purple-400">
                        {part.content}:{' '}
                      </span>
                    );
                  } else if (part.type === 'newline') {
                    return <br key={index} />;
                  } else {
                    return (
                      <span key={index}>
                        {part.content}
                      </span>
                    );
                  }
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DeleteDialog({ isOpen, onConfirm, onCancel, title, message }: DeleteDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4"
      >
        <div className="flex items-center mb-4 text-red-500">
          <AlertCircle className="w-6 h-6 mr-2" />
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          {message}
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Отмена
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Удалить
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function ReelsList({ reels, sourceId, onReelsDeleted, items, onPreventLoadItems, onUpdateContent }: ReelsListProps) {
  const { token } = useAuth();
  const [expandedComments, setExpandedComments] = useState<{ [key: string]: boolean }>({});
  const [expandedVideos, setExpandedVideos] = useState<{ [key: string]: boolean }>({});
  const [selectedReels, setSelectedReels] = useState<string[]>([]);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [normalizedReels, setNormalizedReels] = useState<Reel[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [barPosition, setBarPosition] = useState({ bottom: 0 });
  const [ignorePropsUpdate, setIgnorePropsUpdate] = useState(false);
  
  // Состояние для сортировки
  const [sortBy, setSortBy] = useState<'views' | 'aida' | 'default'>('default');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Ключ для кэша зависит от sourceId, чтобы хранить разные кэши для разных источников
  const cacheKey = `reels_cache_${sourceId || "all"}`;
  
  // Функция для вычисления среднего AIDA балла
  const getAidaScore = (reel: Reel): number => {
    if (!reel.aidaAnalysis) return 0;
    
    const scores = [
      reel.aidaAnalysis.Attention?.Score || reel.aidaAnalysis.attention?.score || 0,
      reel.aidaAnalysis.Interest?.Score || reel.aidaAnalysis.interest?.score || 0,
      reel.aidaAnalysis.Desire?.Score || reel.aidaAnalysis.desire?.score || 0,
      reel.aidaAnalysis.Action?.Score || reel.aidaAnalysis.action?.score || 0
    ].filter(score => score > 0);
    
    return scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
  };
  
  // Функция сортировки
  const sortReels = (reelsToSort: Reel[]): Reel[] => {
    if (sortBy === 'default') return reelsToSort;
    
    return [...reelsToSort].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'views') {
        comparison = a.videoPlayCount - b.videoPlayCount;
      } else if (sortBy === 'aida') {
        const scoreA = getAidaScore(a);
        const scoreB = getAidaScore(b);
        comparison = scoreA - scoreB;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // При смене sourceId (т.е. переходе на другой документ) сбрасываем локальное состояние,
  // иначе может отображаться список рилсов от предыдущего документа.
  useEffect(() => {
    setExpandedComments({});
    setExpandedVideos({});
    setSelectedReels([]);
    setShowMoveDialog(false);
    setShowDeleteDialog(false);
    setIgnorePropsUpdate(false);
    
    // Важно: очищаем текущие рилсы сразу, чтобы не "залипали" данные прошлого документа,
    // даже если новый документ пустой.
    setNormalizedReels([]);
    setIsLoading(true);
  }, [sourceId]);

  // Загрузка кэшированных данных при смене документа (cacheKey зависит от sourceId)
  useEffect(() => {
    loadCachedReels();
  }, [cacheKey]);

  // Обработка новых данных из props
  useEffect(() => {
    if (reels && !ignorePropsUpdate) {
      normalizeReelsData();
    }
  }, [reels, ignorePropsUpdate]);

  // Обработчик прокрутки и изменения размера окна
  useEffect(() => {
    if (selectedReels.length === 0) return;
    
    const updateBarPosition = () => {
      // Получаем высоту видимой области
      const viewportHeight = window.innerHeight;
      
      // Устанавливаем бар в нижней части видимой области окна
      setBarPosition({ bottom: 0 });
      
      // Если есть специфический контейнер, который может влиять на позиционирование,
      // можно делать дополнительные расчеты здесь
    };
    
    // Устанавливаем начальную позицию
    updateBarPosition();
    
    // Добавляем слушатели событий с небольшой задержкой для лучшей производительности
    const handleScroll = () => {
      window.requestAnimationFrame(updateBarPosition);
    };
    
    const handleResize = () => {
      window.requestAnimationFrame(updateBarPosition);
    };
    
    // Добавляем слушатели событий
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    
    // Очистка при размонтировании
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedReels.length]);

  // Загрузка кэшированных рилсов из localStorage
  const loadCachedReels = () => {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          setNormalizedReels(parsedData);
          setIsLoading(false);
        }
      }
    } catch (error) {
      // Ошибка при загрузке кэшированных данных
    }
  };

  // Сохранение нормализованных рилсов в localStorage
  const saveReelsToCache = (reelsData: Reel[]) => {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(reelsData));
    } catch (error) {
      // Ошибка при сохранении данных в кэш
    }
  };

  const normalizeReelsData = () => {
    try {
      if (!reels || !Array.isArray(reels)) {
        return;
      }

      // Используем any для исходных данных, так как они могут прийти в разных форматах от API
      const validReels = reels.filter((reel: any) => 
        reel && 
        typeof reel === 'object'
      ).map((reel: any) => ({
        id: reel.id || reel.Id || '',
        inputUrl: reel.inputUrl || reel.InputUrl || '',
        caption: reel.caption || reel.Caption || '',
        transcription: reel.transcription || reel.Transcription || '',
        videoDescription: reel.videoDescription || reel.VideoDescription || '',
        keyPoint: reel.keyPoint || reel.KeyPoint || reel.keyMessage || reel.KeyMessage || '',
        keyMessage: reel.keyMessage || reel.KeyMessage || reel.keyPoint || reel.KeyPoint || '',
        hook: reel.hook || reel.Hook || '',
        improvedContent: reel.improvedContent || reel.ImprovedContent || '',
        aidaAnalysis: reel.aidaAnalysis || reel.AidaAnalysis || null,
        hashtags: Array.isArray(reel.hashtags || reel.Hashtags) ? [...(reel.hashtags || reel.Hashtags)] : [],
        mentions: Array.isArray(reel.mentions || reel.Mentions) ? [...(reel.mentions || reel.Mentions)] : [],
        url: reel.url || reel.Url || '',
        commentsCount: reel.commentsCount || reel.CommentsCount || 0,
        videoUrl: reel.videoUrl || reel.VideoUrl || '',
        likesCount: reel.likesCount || reel.LikesCount || 0,
        videoPlayCount: reel.videoPlayCount || reel.VideoPlayCount || 0,
        timestamp: reel.timestamp || reel.Timestamp || '',
        ownerFullName: reel.ownerFullName || reel.OwnerFullName || '',
        ownerUsername: reel.ownerUsername || reel.OwnerUsername || '',
        ownerId: reel.ownerId || reel.OwnerId || '',
        followersCount: reel.followersCount || reel.FollowersCount || undefined,
        productType: reel.productType || reel.ProductType || '',
        videoDuration: reel.videoDuration || reel.VideoDuration || 0,
        isSponsored: reel.isSponsored || reel.IsSponsored || false,
        musicInfo: {
          artistName: reel.musicInfo?.artistName || reel.musicInfo?.artist_name || '',
          songName: reel.musicInfo?.songName || reel.musicInfo?.song_name || '',
          usesOriginalAudio: reel.musicInfo?.usesOriginalAudio || false,
          shouldMuteAudio: reel.musicInfo?.shouldMuteAudio || false,
          shouldMuteAudioReason: reel.musicInfo?.shouldMuteAudioReason || '',
          audioId: reel.musicInfo?.audioId || ''
        },
        latestComments: Array.isArray(reel.latestComments || reel.LatestComments) 
          ? (reel.latestComments || reel.LatestComments).map((comment: any) => ({
              text: comment.text || comment.Text || '',
              ownerUsername: comment.ownerUsername || comment.OwnerUsername || '',
              timestamp: comment.timestamp || comment.Timestamp || '',
              repliesCount: comment.repliesCount || comment.RepliesCount || 0,
              likesCount: comment.likesCount || comment.LikesCount || 0,
              replies: Array.isArray(comment.replies || comment.Replies) ? (comment.replies || comment.Replies) : []
            }))
          : []
      }));


      
      // Важно: даже если список пустой — мы обязаны очистить состояние,
      // иначе останется список от предыдущего документа.
      setNormalizedReels(validReels);
      saveReelsToCache(validReels);
      setIsLoading(false);
    } catch (error) {
      console.error('Ошибка нормализации данных:', error);
      setIsLoading(false);
    }
  };

  const toggleComments = (reelUrl: string) => {
    setExpandedComments(prev => ({
      ...prev,
      [reelUrl]: !prev[reelUrl]
    }));
  };

  const toggleVideo = (reelUrl: string) => {
    setExpandedVideos(prev => ({
      ...prev,
      [reelUrl]: !prev[reelUrl]
    }));
  };

  const toggleReelSelection = (reelUrl: string) => {
    setSelectedReels(prev => {
      if (prev.includes(reelUrl)) {
        return prev.filter(url => url !== reelUrl);
      }
      return [...prev, reelUrl];
    });
  };

  // Рекурсивная функция для поиска элемента по ID в древовидной структуре
  const findItemById = (items: KnowledgeItem[], id: string): KnowledgeItem | null => {
    for (const item of items) {
      if (item.id === id) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleMove = async (targetFolderId: string | null, newFileName?: string) => {
    if (!token || !sourceId) {
      return;
    }
    
    if (selectedReels.length === 0) return;
    
    if (!items) {
      return;
    }
    
    setIsProcessing(true);
    setIgnorePropsUpdate(true); // Игнорируем обновления props во время операции
    onPreventLoadItems?.(true); // Блокируем загрузку items в Knowledge
    setProcessingStatus('Подготовка данных...');
    console.log('Начало процесса перемещения рилсов');

    try {
      const selectedReelsData = normalizedReels.filter(reel => selectedReels.includes(reel.url));
      const selectedReelsIds = selectedReelsData.map(reel => reel.id || '').filter(id => id !== '');
      
      console.log(`Выбрано ${selectedReelsIds.length} рилсов для перемещения`);
      
      // Проверяем, что есть ID для перемещения
      if (selectedReelsIds.length === 0) {
        throw new Error('Некоторые выбранные Reels не имеют идентификаторов');
      }
      
      console.log('ID выбранных рилсов:', selectedReelsIds);
      console.log('ID исходной статьи:', sourceId);
      
      const targetItem = targetFolderId ? findItemById(items, targetFolderId) : null;
      
      if (targetItem && targetItem.itemType === 'file' && targetItem.fileType === 'reels') {
        // Если выбран существующий файл типа reels
        setProcessingStatus('Перемещение рилсов в существующую статью...');
        console.log('Перемещение в существующую статью с ID:', targetItem.id);
        
        const moveData = {
          sourceReelsId: sourceId,
          targetReelsId: targetItem.id,
          reelsItemId: selectedReelsIds
        };
        console.log('Данные для запроса перемещения:', moveData);
        
        try {
          const response = await fetch(`${API_URL}/reels/move-item`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(moveData)
          });

          console.log('Статус ответа перемещения:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Текст ошибки:', errorText);
            throw new Error(`Ошибка при перемещении: ${response.status}`);
          }
          
          // Получаем результат операции
          const result = await response.json();
          console.log('Результат перемещения:', result);
          
          // Проверяем успешность перемещения
          // API возвращает объект {sourceReels, targetReels} без поля success
          if (response.ok && result.sourceReels && result.targetReels) {
            // Обновляем список рилсов локально без повторного запроса к серверу
            if (result.sourceReels && result.sourceReels.items) {
              setNormalizedReels(prevReels => {
                // Фильтруем текущие рилсы, удаляя перемещенные
                const updatedReels = prevReels.filter(reel => !selectedReelsIds.includes(reel.id || ''));
                // Сохраняем обновленные данные в локальный кэш
                saveReelsToCache(updatedReels);
                return updatedReels;
              });
            }
            
            // ИСПРАВЛЕНИЕ: НЕ вызываем onUpdateContent после перемещения
            // так как это приводит к save с устаревшими данными из editableContent
            // SignalR событие уже обновит данные у всех участников
          } else {
            console.error('Некорректный ответ от сервера:', result);
            throw new Error('Перемещение не выполнено: неверный формат ответа от сервера');
          }
        } catch (moveError) {
          console.error('Ошибка при перемещении в существующую статью:', moveError);
          throw moveError;
        }
      } else if (newFileName && newFileName.trim()) {
        // Если нужно создать новую статью (есть имя файла)
        let targetReelsId: string | null = null;
        
        setProcessingStatus('Создание новой статьи...');
        console.log('Начинаем создание новой статьи с именем:', newFileName);
        
        const newFile: KnowledgeItem = {
          id: '',
          itemType: 'file',
          fileType: 'reels',
          name: newFileName.trim(),
          content: JSON.stringify([]),
          parentId: targetFolderId
        };
        console.log('Данные новой статьи:', newFile);

        try {
          // Создаем новый файл через API knowledge/save
          const saveResponse = await fetch(`${API_URL}/knowledge/save`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(newFile)
          });

          console.log('Статус создания статьи:', saveResponse.status);
          if (!saveResponse.ok) {
            const errorText = await saveResponse.text();
            console.error('Текст ошибки при создании статьи:', errorText);
            throw new Error(`Ошибка при создании нового файла: ${saveResponse.status}`);
          }

          const savedFile = await saveResponse.json();
          console.log('Полный ответ от API после создания статьи:', savedFile);
          
          // API возвращает объект с полями в PascalCase формате:
          // {
          //   "Id": "0743fd51-17a7-4066-af10-d8639f02d65d",
          //   "ItemType": "file",
          //   "FileType": "reels",
          //   "Name": "сссс",
          //   "Content": "[]",
          //   "Children": [],
          //   "ParentId": null,
          //   "UserId": "67bc3093-414e-4459-817a-c1dd24985a3a"
          // }
          // Получаем ID из ответа, учитывая формат PascalCase
          targetReelsId = savedFile.Id;
          
          console.log('Создана новая статья с ID:', targetReelsId);
          
          // Увеличиваем задержку для гарантированного создания файла на сервере
          setProcessingStatus('Ожидание создания файла на сервере...');
          console.log('Ожидаем 1.5 секунды перед перемещением...');
          await new Promise(resolve => setTimeout(resolve, 1500));
        } catch (error) {
          console.error('Ошибка при создании статьи:', error);
          throw new Error(`Не удалось создать новую статью: ${(error as Error).message || 'Неизвестная ошибка'}`);
        }

        // Если не удалось создать новую статью или идентификатор пустой, прерываем операцию
        if (!targetReelsId) {
          throw new Error('Не удалось получить идентификатор созданной статьи');
        }
        
        // Теперь перемещаем элементы в созданную статью
        setProcessingStatus('Перемещение рилсов в новую статью...');
        console.log('Перемещение в новую статью с ID:', targetReelsId);
        
        try {
          const moveData = {
            sourceReelsId: sourceId,
            targetReelsId: targetReelsId,
            reelsItemId: selectedReelsIds
          };
          console.log('Данные для запроса перемещения:', moveData);
          
          const response = await fetch(`${API_URL}/reels/move-item`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(moveData)
          });

          console.log('Статус ответа перемещения:', response.status);
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Текст ошибки при перемещении:', errorText);
            throw new Error(`Ошибка при перемещении: ${response.status}`);
          }
          
          // Получаем результат операции
          const result = await response.json();
          console.log('Результат перемещения:', result);
          
          // Проверяем успешность перемещения
          // API возвращает объект {sourceReels, targetReels} без поля success
          if (response.ok && result.sourceReels && result.targetReels) {
            // Обновляем список рилсов локально без повторного запроса к серверу
            if (result.sourceReels && result.sourceReels.items) {
              setNormalizedReels(prevReels => {
                // Фильтруем текущие рилсы, удаляя перемещенные
                const updatedReels = prevReels.filter(reel => !selectedReelsIds.includes(reel.id || ''));
                // Сохраняем обновленные данные в локальный кэш
                saveReelsToCache(updatedReels);
                return updatedReels;
              });
            }
            
            // ИСПРАВЛЕНИЕ: НЕ вызываем onUpdateContent после перемещения в новую статью
            // так как это приводит к save с устаревшими данными из editableContent
            // SignalR событие уже обновит данные у всех участников
          } else {
            console.error('Некорректный ответ от сервера:', result);
            throw new Error('Перемещение не выполнено: неверный формат ответа от сервера');
          }
        } catch (error) {
          console.error('Ошибка при перемещении рилсов в новую статью:', error);
          throw new Error(`Не удалось переместить рилсы в новую статью: ${(error as Error).message || 'Неизвестная ошибка'}`);
        }
      } else {
        // Если не выбран файл reels и не указано имя для создания нового файла
        throw new Error('Для перемещения Reels необходимо выбрать существующую статью типа Reels или создать новую статью с указанием названия');
      }
      
      // Принудительно сбрасываем выбранные рилсы и закрываем диалог
      setSelectedReels([]);
      
      // Важно: закрываем диалог в конце всех операций
      console.log('Закрываем диалог перемещения');
      setShowMoveDialog(false);
      
      console.log('Процесс перемещения успешно завершен');
      
      // Примечание: не вызываем onReelsDeleted() для перемещения, 
      // так как это вызывает полную перезагрузку документа.
      // Локальное состояние уже обновлено выше через setNormalizedReels.
    } catch (error) {
      console.error('Ошибка при перемещении:', error);
    } finally {
      // Убедимся, что в любом случае мы сбрасываем статус обработки
      console.log('Завершение процесса перемещения');
      setProcessingStatus('');
      setIsProcessing(false);
      setIgnorePropsUpdate(false); // Возвращаем обработку props updates
      onPreventLoadItems?.(false); // Разблокируем загрузку items в Knowledge
      
      // Принудительно закрываем диалог в случае ошибки
      setTimeout(() => {
        if (showMoveDialog) {
          console.log('Принудительное закрытие диалога');
          setShowMoveDialog(false);
        }
      }, 500);
    }
  };

  const handleDelete = async () => {
    if (!token || !sourceId) {
      return;
    }

    setIsProcessing(true);
    setIgnorePropsUpdate(true); // Игнорируем обновления props во время операции
    onPreventLoadItems?.(true); // Блокируем загрузку items в Knowledge

    try {
      const selectedReelsData = normalizedReels.filter(reel => selectedReels.includes(reel.url));
      const selectedReelsIds = selectedReelsData.map(reel => reel.id);

      const response = await fetch(`${API_URL}/reels/remove-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reelsId: sourceId,
          reelsItemId: selectedReelsIds
        })
      });

      if (!response.ok) {
        throw new Error(`Ошибка при удалении: ${response.status}`);
      }
      
      // Получаем результат операции
      const result = await response.json();
      
      if (result.success) {
        // Обновляем список рилсов локально без повторного запроса к серверу
        if (result.reelsData && result.reelsData.items) {
          // Используем данные с сервера для обновления списка
          const updatedReelsData = result.reelsData.items.map((item: any) => ({
            id: item.id || '',
            inputUrl: item.inputUrl || '',
            caption: item.caption || '',
            transcription: item.transcription || '',
            videoDescription: item.videoDescription || '',
            keyPoint: item.keyPoint || '',
            keyMessage: item.keyMessage || '',
            hook: item.hook || '',
            aidaAnalysis: item.aidaAnalysis || null,
            hashtags: Array.isArray(item.hashtags) ? [...item.hashtags] : [],
            mentions: Array.isArray(item.mentions) ? [...item.mentions] : [],
            url: item.url || '',
            commentsCount: item.commentsCount || 0,
            latestComments: item.latestComments || [],
            videoUrl: item.videoUrl || '',
            likesCount: item.likesCount || 0,
            videoPlayCount: item.videoPlayCount || 0,
            timestamp: item.timestamp || '',
            ownerFullName: item.ownerFullName || '',
            ownerUsername: item.ownerUsername || '',
            ownerId: item.ownerId || '',
            followersCount: item.followersCount || item.FollowersCount || undefined,
            productType: item.productType || '',
            videoDuration: item.videoDuration || 0,
            isSponsored: Boolean(item.isSponsored),
            musicInfo: item.musicInfo || {}
          }));
          
          setNormalizedReels(updatedReelsData);
          
          // Сохраняем обновленные данные в локальный кэш
          saveReelsToCache(updatedReelsData);
        } else {
          // Если сервер не вернул обновленные данные, удаляем выбранные элементы из локального списка
          setNormalizedReels(prevReels => {
            const updatedReels = prevReels.filter(reel => !selectedReelsIds.includes(reel.id));
            saveReelsToCache(updatedReels);
            return updatedReels;
          });
        }
        
        console.log(`Успешно удалены элементы: ${result.removedCount}`);
      }

      setSelectedReels([]);
      setShowDeleteDialog(false);
      
      // ИСПРАВЛЕНИЕ: НЕ вызываем onUpdateContent после удаления
      // так как это приводит к save с устаревшими данными из editableContent
      // SignalR событие уже обновит данные у всех участников
      
      // Примечание: не вызываем onReelsDeleted() для удаления,
      // так как это вызывает полную перезагрузку документа.
      // Локальное состояние уже обновлено выше через setNormalizedReels.
    } catch (error) {
      console.error('Ошибка при удалении:', error);
    } finally {
      setIsProcessing(false);
      setIgnorePropsUpdate(false); // Возвращаем обработку props updates
      onPreventLoadItems?.(false); // Разблокируем загрузку items в Knowledge
    }
  };

  const formatNumber = (num: number | undefined | null): string => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('ru-RU') || '0';
  };

  const formatLikes = (likes: number): string => {
    if (likes === -1) return '-';
    return formatNumber(likes);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Некорректная дата';
    }
  };

  // Обработчик открытия MoveDialog с проверкой items из props
  const handleOpenMoveDialog = async () => {
    // Используем items из props, если доступны
    if (!items || items.length === 0) {
      return;
    }
    setShowMoveDialog(true);
  };

  if (normalizedReels.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500 dark:text-gray-400">Нет рилсов для отображения</p>
      </div>
    );
  }

  // Получаем отсортированные рилсы
  const sortedReels = sortReels(normalizedReels);

  return (
    <>
      {/* Элементы управления сортировкой */}
      <div className="mb-6 flex flex-wrap items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Всего: {sortedReels.length}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Сортировка:</span>
        </div>
        
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'views' | 'aida' | 'default')}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="default">По умолчанию</option>
            <option value="views">По просмотрам</option>
            <option value="aida">По AIDA оценке</option>
          </select>
          
          {sortBy !== 'default' && (
            <button
              onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              title={sortDirection === 'asc' ? 'По возрастанию' : 'По убыванию'}
            >
              {sortDirection === 'asc' ? (
                <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          )}
        </div>
        
        {sortBy === 'aida' && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            (среднее значение AIDA баллов)
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6 mb-20 items-start">
        {sortedReels.map((reel, index) => (
          <div
            key={index}
            className={`w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl overflow-hidden relative border-2 ${
              selectedReels.includes(reel.url) 
                ? 'border-blue-500' 
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <button
              onClick={() => toggleReelSelection(reel.url)}
              className={`absolute top-2 right-2 p-1 rounded-md transition-colors ${
                selectedReels.includes(reel.url)
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Check className="w-6 h-6" />
            </button>

            <div className="p-4">
              <div className="mb-4">
                <div>
                  <a
                    href={reel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-gray-900 dark:text-white hover:underline"
                  >
                    {reel.ownerFullName || 'Автор'}
                  </a>
                  <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    {reel.ownerUsername ? (
                      <a
                        href={`https://www.instagram.com/${reel.ownerUsername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        @{reel.ownerUsername}
                      </a>
                    ) : (
                      <span>@{reel.ownerUsername || 'username'}</span>
                    )}
                    {reel.followersCount !== undefined && reel.followersCount !== null && (
                      <>
                        <span className="text-gray-400 dark:text-gray-500">•</span>
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>{formatNumber(reel.followersCount)} подписчиков</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="w-full max-w-[280px] mx-auto">
                <button
                  onClick={() => toggleVideo(reel.url)}
                  className="w-full p-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                >
                  {expandedVideos[reel.url] ? 'Скрыть видео' : 'Показать видео'}
                </button>
                
                <AnimatePresence>
                  {expandedVideos[reel.url] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-2"
                    >
                      <div className="aspect-[9/16] relative">
                        <video
                          src={reel.videoUrl}
                          controls
                          playsInline
                          preload="metadata"
                          className="absolute inset-0 w-full h-full object-contain bg-black rounded-lg"
                          onError={(e) => {
                            const target = e.target as HTMLVideoElement;
                            target.insertAdjacentHTML(
                              'afterend',
                              `<div class="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-70 text-white p-4 text-center rounded-lg">
                                <div>
                                  <div class="mb-2">❌ Ошибка загрузки видео</div>
                                  <div class="text-sm opacity-80">Видео недоступно или удалено</div>
                                </div>
                              </div>`
                            );
                            target.style.display = 'none';
                          }}
                        >
                          Ваш браузер не поддерживает видео.
                        </video>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="mt-4 grid grid-cols-4 gap-3 text-center text-sm">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
                    <Heart className="w-4 h-4 text-red-500" />
                    {formatLikes(reel.likesCount)}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
                    <Eye className="w-4 h-4 text-blue-500" />
                    {formatNumber(reel.videoPlayCount)}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
                    <MessageSquare className="w-4 h-4 text-purple-500" />
                    {formatNumber(reel.commentsCount)}
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
                    <Target className="w-4 h-4 text-green-500" />
                    <span className="text-xs">
                      {getAidaScore(reel) > 0 ? getAidaScore(reel).toFixed(1) : '—'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">AIDA</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-sm text-gray-900 dark:text-white">
                  {reel.caption}
                </div>
                <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(reel.timestamp)}
                </div>
              </div>

              {reel.musicInfo && (
                <div className="mt-4 text-sm">
                  <div className="flex items-center">
                    <Music className="w-4 h-4 text-pink-500 dark:text-pink-400 mr-2" />
                    <div className="font-medium text-gray-900 dark:text-white">
                      {reel.musicInfo.song_name || reel.musicInfo.songName}
                    </div>
                  </div>
                  <div className="ml-6 text-gray-500 dark:text-gray-400">
                    {reel.musicInfo.artist_name || reel.musicInfo.artistName}
                  </div>
                </div>
              )}

              {((reel.latestComments && reel.latestComments.length > 0) || (reel.comments && reel.comments.length > 0)) && (
                <div className="mt-4">
                  <button
                    onClick={() => toggleComments(reel.url)}
                    className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Комментарии ({formatNumber(reel.commentsCount)})
                  </button>
                  
                  <AnimatePresence>
                    {expandedComments[reel.url] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="mt-2 space-y-2 overflow-hidden"
                      >
                        {(reel.latestComments || reel.comments || []).map((comment, i) => (
                          <div key={i} className="text-sm">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {comment.ownerUsername}
                            </span>
                            <span className="ml-2 text-gray-700 dark:text-gray-300">
                              {comment.text}
                            </span>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(comment.timestamp)}
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center mb-1">
                  <Zap className="w-3 h-3 mr-1 text-blue-500 dark:text-blue-400" />
                  <span className="text-xs font-medium text-blue-500 dark:text-blue-400">Ключевая мысль:</span>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {reel.keyPoint || reel.keyMessage || "Не указан"}
                </div>
              </div>

              {reel.hook && (
                <div className="mt-3">
                  <div className="flex items-center mb-1">
                    <Target className="w-3 h-3 mr-1 text-green-500 dark:text-green-400" />
                    <span className="text-xs font-medium text-green-500 dark:text-green-400">Хук:</span>
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {reel.hook}
                  </div>
                </div>
              )}
              
              <ReelAnalysisDetails reel={reel} />
              <ReelImprovedContent reel={reel} />
              <ReelTranscriptionDetails reel={reel} />
            </div>
          </div>
        ))}
      </div>

      {/* Пространство для защиты от перекрытия контента в нижней части списка */}
      {selectedReels.length > 0 && (
        <div className="w-full h-20 mt-6"></div>
      )}

      {selectedReels.length > 0 && createPortal(
        <div 
          className="fixed z-[9999] shadow-lg select-none" 
          style={{ 
            position: 'fixed', 
            bottom: `${barPosition.bottom}px`, 
            left: 0, 
            right: 0,
            zIndex: 9999,
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-t-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between shadow-lg">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3 sm:mb-0">
              {isProcessing ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {processingStatus || 'Обработка...'}
                </div>
              ) : (
                `Выбрано: ${selectedReels.length}`
              )}
            </span>
            <div className="flex items-center gap-4 w-full sm:w-auto justify-center sm:justify-end">
              <button
                onClick={handleOpenMoveDialog}
                disabled={isProcessing || !token}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Move className="w-4 h-4 mr-2" />
                {!token ? 'Требуется авторизация' : (isProcessing ? 'Обработка...' : 'Переместить')}
              </button>
              {sourceId && (
                <button
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isProcessing || !token}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {!token ? 'Требуется авторизация' : (isProcessing ? 'Обработка...' : 'Удалить')}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showMoveDialog && createPortal(
        <MoveDialog
          isOpen={showMoveDialog}
          onClose={() => setShowMoveDialog(false)}
          onMove={handleMove}
          items={items || []}
          currentItem={{
            id: sourceId || 'temp',
            itemType: 'file',
            fileType: 'reels',
            name: 'Новый файл',
            parentId: null
          }}
        />,
        document.body
      )}

      {showDeleteDialog && createPortal(
        <DeleteDialog
          isOpen={showDeleteDialog}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteDialog(false)}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить выбранные Reels (${selectedReels.length} шт.)?`}
        />,
        document.body
      )}
    </>
  );
}
